import { faTrash, faUpload } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { ChangeEvent, FC, ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'src/app/hooks';
import { addFile, getFileUrl, removeFile } from 'src/features/globalSlice';
import { useFileSelector } from 'src/utils/useFileSelector';

import Button from '../Button/Button';
import Tooltip from '../Tooltip/Tooltip';
import classes from './UploadPicture.module.scss';
import UploadPictureSkeleton from './UploadPictureSkeleton';

export interface UploadPictureProps {
  imageComponent: ReactNode;
  uploadTooltip?: string;
  enableRemove?: boolean;
  className?: string;
  error?: string;
  loading?: boolean;
  imageId?: string;
}

const UploadPicture: FC<UploadPictureProps> = ({
  imageComponent,
  uploadTooltip,
  enableRemove = false,
  className,
  error,
  loading = false,
  imageId,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  const image = useFileSelector({ id: imageId })?.[0];

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(getFileUrl({ file: e.target?.files?.[0], id: imageId }));
  };

  return (
    <div
      className={classNames(classes.Container, className, {
        [classes.Loading]: loading,
      })}
    >
      {!loading ? (
        <section>
          {imageComponent}
          <div>
            <input
              onChange={onFileChange}
              type="file"
              name="profile"
              accept="image/*"
              ref={inputRef}
            />
            <Tooltip
              tooltipText={uploadTooltip || ''}
              disabled={!uploadTooltip}
            >
              <Button
                onClick={() => inputRef.current?.click()}
                startIcon={<FontAwesomeIcon icon={faUpload} />}
              >
                {t('common:upload')}
              </Button>
            </Tooltip>
            <Button
              onClick={() => {
                if (image?.url) {
                  dispatch(removeFile({ id: imageId }));
                } else {
                  dispatch(addFile({ id: imageId, url: null }));
                }

                if (inputRef.current) {
                  inputRef.current.value = null;
                }
              }}
              disabled={!enableRemove}
              startIcon={<FontAwesomeIcon icon={faTrash} />}
            >
              {t('common:remove')}
            </Button>
          </div>
        </section>
      ) : (
        <UploadPictureSkeleton />
      )}
      {error && !loading && <p>{error}</p>}
    </div>
  );
};

export default UploadPicture;
